import { useEffect, useRef } from 'react';

interface SignaturePadProps {
  value: string;
  onChange(value: string): void;
}

export function SignaturePad({ value, onChange }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasInk = useRef(Boolean(value));
  const activePointerId = useRef<number | null>(null);
  const activeTouchId = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    hasInk.current = Boolean(value);
    if (!value) return;
    const image = new Image();
    image.onload = () => context.drawImage(image, 0, 0, canvas.width, canvas.height);
    image.src = value;
  }, [value]);

  function point(clientX: number, clientY: number) {
    const canvas = canvasRef.current!;
    const bounds = canvas.getBoundingClientRect();
    return {
      x: (clientX - bounds.left) * (canvas.width / bounds.width),
      y: (clientY - bounds.top) * (canvas.height / bounds.height),
    };
  }

  function startAt(clientX: number, clientY: number) {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    drawing.current = true;
    const current = point(clientX, clientY);
    context.beginPath();
    context.moveTo(current.x, current.y);
    context.strokeStyle = '#111827';
    context.lineWidth = 3;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineTo(current.x + 0.01, current.y + 0.01);
    context.stroke();
    hasInk.current = true;
  }

  function moveTo(clientX: number, clientY: number) {
    if (!drawing.current) return;
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;
    const current = point(clientX, clientY);
    context.lineTo(current.x, current.y);
    context.stroke();
    hasInk.current = true;
  }

  function saveDrawing() {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    if (canvas && hasInk.current) onChangeRef.current(canvas.toDataURL('image/png'));
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const appleTouchDevice = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const pointerStart = (event: PointerEvent) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault();
      activePointerId.current = event.pointerId;
      try { canvas.setPointerCapture?.(event.pointerId); } catch { /* Safari poate refuza capturarea. */ }
      startAt(event.clientX, event.clientY);
    };
    const pointerMove = (event: PointerEvent) => {
      if (!drawing.current || activePointerId.current !== event.pointerId) return;
      event.preventDefault();
      const points = typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [event];
      points.forEach((current) => moveTo(current.clientX, current.clientY));
    };
    const pointerFinish = (event: PointerEvent) => {
      if (!drawing.current || activePointerId.current !== event.pointerId) return;
      event.preventDefault();
      try {
        if (canvas.hasPointerCapture?.(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      } catch { /* Captura poate fi deja pierdută. */ }
      activePointerId.current = null;
      saveDrawing();
    };

    const findTouch = (touches: TouchList) => Array.from(touches).find((touch) => touch.identifier === activeTouchId.current);
    const start = (event: globalThis.TouchEvent) => {
      if (activeTouchId.current !== null || !event.changedTouches[0]) return;
      event.preventDefault();
      const touch = event.changedTouches[0];
      activeTouchId.current = touch.identifier;
      startAt(touch.clientX, touch.clientY);
    };
    const move = (event: globalThis.TouchEvent) => {
      const touch = findTouch(event.touches);
      if (!touch || !drawing.current) return;
      event.preventDefault();
      moveTo(touch.clientX, touch.clientY);
    };
    const finish = (event: globalThis.TouchEvent) => {
      const touch = findTouch(event.changedTouches);
      if (activeTouchId.current === null || !drawing.current) return;
      if (touch && touch.identifier !== activeTouchId.current) return;
      event.preventDefault();
      activeTouchId.current = null;
      saveDrawing();
    };

    // Safari iOS declara Pointer Events, dar unele versiuni pierd desenul pe canvas
    // dupa pointer capture. Pe dispozitivele Apple tactile folosim direct Touch Events.
    if (appleTouchDevice || !('PointerEvent' in window)) {
      canvas.addEventListener('touchstart', start, { passive: false });
      canvas.addEventListener('touchmove', move, { passive: false });
      canvas.addEventListener('touchend', finish, { passive: false });
      canvas.addEventListener('touchcancel', finish, { passive: false });
    } else {
      canvas.addEventListener('pointerdown', pointerStart, { passive: false });
      canvas.addEventListener('pointermove', pointerMove, { passive: false });
      canvas.addEventListener('pointerup', pointerFinish, { passive: false });
      canvas.addEventListener('pointercancel', pointerFinish, { passive: false });
    }
    return () => {
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', finish);
      canvas.removeEventListener('touchcancel', finish);
      canvas.removeEventListener('pointerdown', pointerStart);
      canvas.removeEventListener('pointermove', pointerMove);
      canvas.removeEventListener('pointerup', pointerFinish);
      canvas.removeEventListener('pointercancel', pointerFinish);
    };
  }, []);

  function clear() {
    const canvas = canvasRef.current;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
    onChange('');
  }

  return <section className="signature-field">
    <div className="signature-field__header">
      <div><strong>Semnătură client</strong><small>Opțională. Semnează cu degetul în zona de mai jos.</small></div>
      {value && <button type="button" className="btn btn-secondary signature-clear" onClick={clear}>Șterge</button>}
    </div>
    <canvas
      ref={canvasRef}
      width={600}
      height={190}
      aria-label="Zonă pentru semnătura clientului"
    />
    {value && <span className="signature-saved">✓ Semnătura a fost înregistrată</span>}
  </section>;
}
